import { useMemo } from "react";

import { Interval } from "date-fns";
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  startOfMonth,
} from "date-fns/esm";

import { Activity } from "@mapmycustomers/shared/types/entity";

import RecapRange, { recapRangeIntervalMap } from "@app/enum/preview/RecapRange";
import { parseApiDate } from "@app/util/parsers";

type IntervalGenerator = (interval: Interval) => Date[] | undefined;

const rangeIntervalGeneratorMap: Record<RecapRange, IntervalGenerator> = {
  [RecapRange.ALL_TIME]: eachMonthOfInterval,
  [RecapRange.LAST_MONTH]: eachDayOfInterval,
  [RecapRange.LAST_QUARTER]: eachWeekOfInterval,
  [RecapRange.LAST_WEEK]: eachDayOfInterval,
  [RecapRange.LAST_YEAR]: eachMonthOfInterval,
  [RecapRange.NEXT_MONTH]: eachDayOfInterval,
  [RecapRange.NEXT_QUARTER]: eachWeekOfInterval,
  [RecapRange.NEXT_WEEK]: eachDayOfInterval,
  [RecapRange.THIS_MONTH]: eachDayOfInterval,
  [RecapRange.THIS_QUARTER]: eachWeekOfInterval,
  [RecapRange.THIS_WEEK]: eachDayOfInterval,
  [RecapRange.THIS_YEAR]: eachMonthOfInterval,
};

const useXAxisDates = (recapRange: RecapRange, activities: Activity[]): Date[] | undefined =>
  useMemo(() => {
    let res = recapRangeIntervalMap[recapRange];

    if (recapRange === RecapRange.ALL_TIME) {
      const oldestActivity = activities.reduce<Activity | undefined>((oldest, current) => {
        if (!current.startAt) {
          return oldest;
        }
        if (!oldest?.startAt) {
          return current;
        }
        return oldest.startAt > current.startAt ? current : oldest;
      }, undefined);

      if (!oldestActivity) {
        return undefined;
      }

      res = {
        end: endOfMonth(Date.now()),
        start: startOfMonth(parseApiDate(oldestActivity.startAt!)),
      } as Interval;
      if (res.start > res.end) {
        // perhaps all activities are in the future
        return undefined;
      }
    }

    if (res) {
      return recapRange && rangeIntervalGeneratorMap && rangeIntervalGeneratorMap[recapRange](res);
    }
    return undefined;
  }, [recapRange, activities]);

export default useXAxisDates;
